<template>
  <v-expansion-panel class="elevation-1 my-1">
    <v-expansion-panel-header>
      <v-row class="align-center">
        <v-col cols="3">
          {{ $t("commons.section").toUpperCase() }} : {{ limit(section.title, 45) }}
        </v-col>
        <v-col cols="7">{{ limit(section.description, 100) }}</v-col>
        <v-col
          cols="2"
          class="d-flex justify-end"
        >
          <!-- Bouton de duplication -->
          <v-btn
            color="primary"
            fab
            text
            small
            @click.stop="handleDuplicateSection"
            :disabled="disabled"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
          <avs-deleteButton
            @click.stop="handleDeleteSection()"
            :disabled="disabled"
            :spantext="$t('actions.delete', { entity: $tc('commons.section', 1).toLowerCase() })"
          />
        </v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-card-text>
        <v-text-field
          v-model="section.title"
          :rules="rules.title"
          :label="$t('commons.title')"
          counter="64"
          maxlength="64"
          dense
          outlined
        ></v-text-field>

        <v-textarea
          v-model="section.description"
          :rules="rules.description"
          :label="$t('commons.description')"
          dense
          outlined
        ></v-textarea>
      </v-card-text>

      <v-card-text>
        <div class="d-flex">
          <avs-button
            class="mr-3"
            :disabled="disabled"
            color="blue white--text"
            iconI="mdi-file-document-plus-outline"
            :spantext="$t('actions.create', { entity: $t('commons.new_field').toLowerCase() })"
            menu
          >
            <template v-slot:name>
              {{ $t("actions.create", { entity: $t("commons.field").toLowerCase() }) }}
            </template>
            <template v-slot:list>
              <v-list>
                <v-list-item
                  v-for="(item, index) in basecomponents"
                  :key="index"
                  link
                  @click="$event => handleOpenEditDialogByComponent(item)"
                >
                  <v-list-item-title>{{ get_type(item.name) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </template>
          </avs-button>
          <avs-button
            color="blue white--text"
            :disabled="disabled"
            iconI="mdi-file-document-refresh-outline"
            :spantext="$t('actions.pick', { entity: $t('commons.existing_field').toLowerCase() })"
            @click.stop="handleExistingField"
          >
            <template v-slot:name>
              {{ $t("actions.pick", { entity: $t("commons.field").toLowerCase() }) }}
            </template>
          </avs-button>
        </div>
        <v-flex>
          <v-list>
            <draggable
              v-bind="options"
              v-model="section.fields"
            >
              <transition-group
                type="transition"
                name="flip-list"
              >
                <v-list-item
                  v-for="(item, index) in section.fields"
                  :key="item.name"
                  class="v-sheet--outlined"
                >
                  <v-list-item-content>
                    <v-row style="justify-content: space-between; align-items: center">
                      <v-col cols="3">
                        <v-icon color="primary">mdi-drag</v-icon> {{ item.name }}</v-col
                      >
                      <v-col cols="3"
                        >{{ $t("commons.type").toUpperCase() }} : {{ item.type.name }}</v-col
                      >

                      <v-col
                        cols="2"
                        style="display: flex; justify-content: flex-end"
                      >
                        <v-btn
                          color="primary"
                          fab
                          text
                          small
                          @click.stop="handleEditField(item, index)"
                          :disabled="disabled"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <!-- Bouton de duplication -->
                        <v-btn
                          color="primary"
                          fab
                          text
                          small
                          @click.stop="handleDuplicateField(item, index)"
                          :disabled="disabled"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                        <avs-deleteButton
                          @click.stop="handleDeleteField(index)"
                          :disabled="disabled"
                        />
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </transition-group>
            </draggable>
          </v-list>
        </v-flex>
      </v-card-text>
    </v-expansion-panel-content>

    <!-- 
      Here we check if null because else the dialog is rendered and does not initialize values 
      cf this link : https://stackoverflow.com/questions/56114491/cant-get-component-data-initialization-with-prop-value-to-work   
    -->
    <EditFieldDialog
      v-if="this.editedField !== null"
      :companyId="companyId"
      :isEnabled="editFieldDialogIsOpen"
      :rulesUpdate="rules.editFieldDialog"
      :title="title"
      :value="editedField"
      @cancel="handleCloseDialog"
      @save="handleSaveField"
    />

    <v-dialog
      v-model="editDialogIsOpen"
      width="40vw"
    >
      <component
        v-model="selectedItem"
        :component="selectedComponent"
        :hasUniqueName="hasUniqueName"
        :inTemplate="true"
        :is="editDialogContent"
        @cancel="handleCancel"
        @error="handleError"
        @success="handleSuccess"
      />
    </v-dialog>
  </v-expansion-panel>
</template>

<script>
import draggable from "vuedraggable"
import { COMPONENT_ENUM } from "@/utils/consts"
import { limit } from "@/utils/string"
// import { max64, required, slug, mkUnique } from "@/utils/validator"
import { max64, required, mkUnique } from "@/utils/validator"
import EditFieldDialog from "./EditFieldDialog.vue"
import SelectorFieldEditor from "@/components/ri3/documents/fieldtypes/SelectorFieldEditor.vue"
import TableFieldEditor from "@/components/ri3/documents/fieldtypes/TableFieldEditor.vue"

export default {
  components: { draggable, EditFieldDialog, SelectorFieldEditor, TableFieldEditor },
  props: {
    companyId: { type: Number, required: true },
    value: {
      type: Object,
      default: () => ({
        title: "",
        description: "",
        fields: []
      })
    },
    disabled: Boolean,
    index: Number
  },
  data() {
    return {
      editFieldDialogIsOpen: false,
      editedField: null,
      editedFieldIndex: null,
      title: null,

      basecomponents: Object.values(COMPONENT_ENUM),
      editDialogIsOpen: false,
      editDialogContent: null,
      selectedItem: undefined,
      selectedComponent: null,
      items: []
    }
  },
  computed: {
    options() {
      return {
        animation: 500,
        disabled: this.disabled,
        ghostClass: "ghost"
      }
    },
    rules() {
      const namelist = this.section.fields
        .filter(e => e.name != this.editedField?.name)
        .map(e => e.name)

      return {
        //title: [required, slug, max64],
        title: [required, max64],
        description: [],
        // editFieldDialog: { name: [required, slug, mkUnique(namelist, "name")] }
        editFieldDialog: { name: [required, mkUnique(namelist, "name")] }
      }
    },
    hasUniqueName: {
      get() {
        const names = this.items.filter(e => e.name !== this.selectedItem?.name).map(e => e.name)
        return mkUnique(names, "name")
      }
    },
    section: {
      get() {
        return Object.assign(this.value)
      },
      set(newValue) {
        this.$emit("input", newValue)
      }
    }
  },
  methods: {
    limit,
    handleDeleteSection() {
      this.$emit("delete", this.index)
    },
    handleDuplicateSection() {
      // Création d'une copie de la section actuelle
      const duplicatedSection = {
        title: this.value.title + " (copie)",
        description: this.value.description,
        fields: [...this.value.fields] // Copie des champs
      }
      this.$emit("duplicate", duplicatedSection)
    },
    handleDuplicateField(field, index) {
      // Copie profonde pour éviter de modifier l'original
      const duplicatedField = JSON.parse(JSON.stringify(field))

      // Assurer un nom unique pour éviter les conflits
      duplicatedField.name = `${field.name}_copy`

      // Supprimer l'ID pour laisser le backend en générer un nouveau
      if (duplicatedField.id) {
        delete duplicatedField.id
      }

      // Insérer juste après l'original
      this.section.fields.splice(index + 1, 0, duplicatedField)

      // Réassigner correctement les positions pour éviter les conflits
      this.section.fields.forEach((field, i) => {
        field.position = i
      })

      // Forcer Vue à détecter la mise à jour
      this.$set(this.section, "fields", [...this.section.fields])
    },

    handleExistingField() {
      this.title = this.$t("actions.new", { entity: this.$t("commons.field").toLowerCase() })
      this.editedField = { name: "", type: null }
      this.editedFieldIndex = null
      this.editFieldDialogIsOpen = true
    },
    handleEditField(field, index) {
      this.title = this.$t("actions.edit", { entity: this.$t("commons.field").toLowerCase() })
      this.editedField = field
      this.editedFieldIndex = index
      this.editFieldDialogIsOpen = true
    },
    handleSaveField(update) {
      if (this.editedFieldIndex !== null) {
        const oldValue = this.section.fields[this.editedFieldIndex]
        const newValue = Object.assign(oldValue, update)
        this.section.fields[this.editedFieldIndex] = newValue
      } else {
        this.section.fields.push(update)
      }
      this.handleCloseDialog()
    },
    handleDeleteField(index) {
      this.section.fields.splice(index, 1)
    },
    handleCloseDialog() {
      this.editFieldDialogIsOpen = false
      this.editedField = null
      this.editedFieldIndex = null
      this.title = null
    },
    handleOpenEditDialogByComponent(component) {
      this.selectedComponent = component
      switch (this.selectedComponent.id) {
        case COMPONENT_ENUM.SELECTOR.id:
        case COMPONENT_ENUM.CHECKBOX.id:
          this.editDialogContent = SelectorFieldEditor
          break
        case COMPONENT_ENUM.TABLE.id:
          this.editDialogContent = TableFieldEditor
          break
      }

      this.editDialogIsOpen = true
    },
    handleCancel() {
      this.selectedComponent = null
      this.selectedItem = undefined
      this.editDialogIsOpen = false
      this.editDialogContent = null
    },
    handleError(message) {
      this.send(message || this.$t("messages.error.failed_admin", { role: "INSPECTOR" }), "error")
    },
    handleSuccess(field) {
      this.handleCancel()
      this.handleSaveField(field)
      this.$nextTick(() => {
        this.send(this.$t("messages.success.operation"), "success")
      })
    },
    get_type(item) {
      if (item.toLowerCase() == "selector") {
        return this.$t("commons.selector")
      } else if (item.toLowerCase() == "table") {
        return this.$t("commons.table")
      } else if (item.toLowerCase() == "checkbox") {
        return this.$t("commons.checkbox")
      } else if (item.toLowerCase() == "textfield") {
        return this.$t("commons.textfield")
      } else if (item.toLowerCase() == "imagefield") {
        return this.$t("commons.imagefield")
      } else if (item.toLowerCase() == "textarea") {
        return this.$t("commons.textarea")
      } else {
        return this.$t("commons.datefield")
      }
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    }
  }
}
</script>
